/**
 * D-048 / D-049: kütük kayıtlarının belgeleri (stok paketi, sarf lotu, kaynakçı, alet, WPS).
 * D-062: NDT inspektörünün sertifikaları da burada (kütük "ndt_inspektor", tek tür "ndt_sertifika").
 * D-064: NDT sertifikası satırı yöntemini (MT, PT, UT, VT, RT) ve seviyesini (1, 2, 3) taşır; yeni satırda ikisi de zorunludur
 * (`belgeZorunluHatasi`). Rapor yüklenirken imzalayanın yeterliliği bu iki alandan ve geçerlilik sonundan ölçülür: ndt-yeterlilik.ts.
 *
 * Belge satırı `kutuk_belge` tablosunda; kayda bağ düz metin anahtardır (stok_paket: paket id · sarf: lot · ndt_inspektor:
 * inspektör id · öteki: kod). Türler sabit kısa kodlardır; ekran adı web'de `cevir` ile gelir. Saf: hem API hem web kullanır,
 * tarihler JSON'da ISO metin.
 *
 * D-074 Faz 2 (K16, K23): belge satırı "Superseded" (yerine yenisi geldi) işaretini taşır (`yenisiGeldiTs`, `yenisiGeldiKim`). Güncel
 * belge = kaldırılmamış ve işaretsiz; OASIS yalnız güncel belgeleri kullanır.
 *
 * D-074 Faz 3 (K20): altı yeni tür (3.2 ve fatura stok paketinde, kabul formu lotta, CE attestation ve üretici test raporu alette,
 * dolgu onay sayfası WPS'te). Okunmaz; elle dosyalanır. 3.2 rozet ve Riser Documentation'da 3.1 gibidir; alette kalibrasyon dışı
 * türlerde bitiş tarihi sorulmaz.
 */
/** Kütük başına geçerli belge türleri (statik sayfalardaki "Type" seçimiyle aynı sırada). */
export const BELGE_TURLERI = {
    stok_paket: ["en10204_31", "coc", "po", "teslim", "en10204_32", "fatura"],
    sarf: ["en10204_31", "po", "kabul_formu"],
    kaynakci: ["wpq", "sureklilik"],
    /** D-062: tek tür. D-064: yöntem ve seviye tür değildir; satırın "yontem" ve "seviye" alanlarında durur (NDT_YONTEMLERI, NDT_SEVIYELERI). */
    ndt_inspektor: ["ndt_sertifika"],
    alet: ["kalibrasyon", "ce_uygunluk", "uretici_test"],
    wps: ["wps", "pqr", "dolgu_onayi"],
};
/** "Type" seçiminin ve olay metninin tür adı (İngilizce; "EN 10204 3.1" korunan değer adı, metni aynen kalır). */
export const BELGE_TURU_ADI = {
    stok_paket: {
        en10204_31: "Material certificate (EN 10204 3.1)",
        coc: "Certificate of conformity (CoC)",
        po: "PO",
        teslim: "Delivery note / packing list",
        en10204_32: "Material certificate (EN 10204 3.2)",
        fatura: "Commercial invoice",
    },
    sarf: { en10204_31: "Consumable certificate (EN 10204 3.1)", po: "PO", kabul_formu: "Consumable receiving inspection form" },
    kaynakci: { wpq: "Welder qualification (WPQ / WPQR)", sureklilik: "Continuity / prolongation record" },
    ndt_inspektor: { ndt_sertifika: "NDT certificate" },
    alet: { kalibrasyon: "Calibration certificate", ce_uygunluk: "CE attestation of conformity", uretici_test: "Manufacturer test report" },
    wps: { wps: "WPS", pqr: "PQR", dolgu_onayi: "Alternative filler metal approval" },
};
/** Tablo çipindeki kısa tür adı. */
export const BELGE_TURU_KISA = {
    en10204_31: "3.1",
    coc: "CoC",
    po: "PO",
    teslim: "Delivery note",
    wpq: "WPQR",
    sureklilik: "Continuity",
    ndt_sertifika: "NDT certificate",
    kalibrasyon: "Calibration",
    wps: "WPS",
    pqr: "PQR",
    // D-074 Faz 3 (K20)
    en10204_32: "3.2",
    fatura: "Invoice",
    kabul_formu: "Receiving form",
    ce_uygunluk: "CE attestation",
    uretici_test: "Manufacturer test",
    dolgu_onayi: "Filler approval",
};
/**
 * Kütüğe özgü kolonlar (Type · PDF · Pages · Doc no. her kütükte ortak; ardından bunlar; en sonda Remove). Statik sayfalardaki sırayla.
 * Stok paketinde heat / lot / SN ve PO no. paketin alanlarıdır, belge satırında sorulmaz (D-048).
 */
export const BELGE_KOLONLARI = {
    stok_paket: [],
    sarf: [
        { alan: "duzenleyen", etiket: "Manufacturer / supplier" },
        { alan: "tarih", etiket: "Date" },
        { alan: "poNo", etiket: "PO no." },
    ],
    kaynakci: [
        { alan: "standart", etiket: "Standard" },
        { alan: "tarih", etiket: "Date" },
        { alan: "gecerlilikSonu", etiket: "End of validity" },
        { alan: "not", etiket: "Note" },
    ],
    // D-062: "Standard" kolonu yok. D-064: Method ve Level (yeni sertifikada zorunlu seçim). Geçerlilik sonu boş bırakılabilir; kayıt
    // düzeyinde tarih kuralı yok, süreye yalnız rapor yüklenirken ekran bakar (ndt-yeterlilik.ts).
    ndt_inspektor: [
        { alan: "yontem", etiket: "Method" },
        { alan: "seviye", etiket: "Level" },
        { alan: "tarih", etiket: "Date" },
        { alan: "gecerlilikSonu", etiket: "End of validity" },
        { alan: "not", etiket: "Note" },
    ],
    alet: [
        { alan: "duzenleyen", etiket: "Lab" },
        { alan: "tarih", etiket: "Date" },
        { alan: "gecerlilikSonu", etiket: "Calibration end" },
    ],
    wps: [],
};
/**
 * Belge satırının girilebilen alanları: "belgeNo" + kütüğün kolonları; kaynakçının süreklilik kaydında yalnız belgeNo, tarih, not;
 * alette kalibrasyon dışındaki türlerde (CE attestation, üretici test raporu) bitiş tarihi yok.
 */
export function belgeAlanlari(kutuk, tur) {
    if (kutuk === "kaynakci" && tur === "sureklilik")
        return ["belgeNo", "tarih", "not"];
    // D-074 Faz 3 (K20): CE attestation ve üretici test raporu kalibrasyon değildir; bitiş tarihi (Calibration end) sorulmaz.
    if (kutuk === "alet" && tur !== "kalibrasyon")
        return ["belgeNo", "duzenleyen", "tarih"];
    return ["belgeNo", ...BELGE_KOLONLARI[kutuk].map((k) => k.alan)];
}
/** Kaynakçı yeterliliğinin standardı (WPQ / WPQR satırında seçim). */
export const KAYNAKCI_STANDARTLARI = ["ASME IX QW-484", "EN ISO 9606-1"];
/** D-064: NDT sertifikasının yöntemi (sertifika satırında seçim). Bekir'in yazdığı sıra; değer adları veride kalıcıdır, yeniden adlandırılmaz. */
export const NDT_YONTEMLERI = ["MT", "PT", "UT", "VT", "RT"];
/** D-064: NDT sertifikasının seviyesi (metin tutulur; karşılaştırmada Number(seviye)). */
export const NDT_SEVIYELERI = ["1", "2", "3"];
/** D-064: NDT raporunu imzalamak için en az seviye (Seviye 3 de yeter). Seviye 1 kayda girilebilir ama imza için yetmez. */
export const NDT_IMZA_EN_AZ_SEVIYE = 2;
/**
 * D-064: belge satırının zorunlu seçimleri (ekran ve sunucu aynısını okur). Yalnız kütük "ndt_inspektor", tür "ndt_sertifika": yöntem
 * listeden değilse "select the method"; yöntem tamamsa seviye listeden değilse "select the level"; yoksa null. Öteki kütük ve türlerde null.
 */
export function belgeZorunluHatasi(kutuk, g) {
    if (kutuk !== "ndt_inspektor" || g.tur !== "ndt_sertifika")
        return null;
    if (!NDT_YONTEMLERI.includes(g.yontem ?? ""))
        return "select the method";
    if (!NDT_SEVIYELERI.includes(g.seviye ?? ""))
        return "select the level";
    return null;
}
/**
 * Kaydın tarihini besleyen tür (D-049): kaynakci → wpq (End of validity), alet → kalibrasyon (Calibration end). NDT inspektöründe yok
 * (D-062; D-064 değiştirmez: sertifikanın süresine kayıt düzeyinde değil, rapor yüklenirken bakılır).
 */
export const BELGE_TARIH_TURU = { kaynakci: "wpq", alet: "kalibrasyon" };
/** D-074 Faz 2: kaydın tarih alanının ekran adı (Registers kolon adıyla aynı). */
export const BELGE_TARIH_ALANI_ADI = { kaynakci: "End of validity", alet: "Calibration end" };
/** `dokuman.aile` değerleri: 3.1 "Product arrived" ile aynı aile; öteki Registers yüklemeleri; eski stok sertifikaları (D-032). */
export const BELGE_AILESI = { en10204_31: "EN10204-3.1", kutuk: "Register document", eskiStok: "Stock certificate" };
/** D-074 Faz 2: belge güncel mi (kaldırılmamış ve yerine yenisi gelmemiş). */
export const guncelBelgeMi = (b) => !b.kaldirildiTs && !b.yenisiGeldiTs;
const ms = (v) => (v === null ? null : new Date(v).getTime());
/**
 * En yeni belge: sıralama anahtarı belge tarihi (Date), boşsa yükleme zamanı (ts); anahtar azalan, eşitse ts azalan. Date isteğe
 * bağlı olduğu için tarihsiz belge sona atılmaz: sonradan yalnız End of validity / Calibration end girilerek eklenen sertifika eklendiği
 * günle sıralanır ve daha eski tarihli belgenin önüne geçer (D-049 "sertifika yüklenince geçerlilik güncellenir").
 */
export function enYeniTarihBelgesi(belgeler) {
    const anahtar = (b) => ms(b.tarih) ?? ms(b.ts) ?? 0;
    let en = null;
    for (const b of belgeler) {
        if (!en) {
            en = b;
            continue;
        }
        const ba = anahtar(b);
        const ea = anahtar(en);
        const dahaYeni = ba !== ea ? ba > ea : (ms(b.ts) ?? 0) > (ms(en.ts) ?? 0);
        if (dahaYeni)
            en = b;
    }
    return en;
}
/**
 * D-074 Faz 2 (K16, K23): "Mark as superseded" (isaret true) ya da "Mark as current" (false) yapılırsa D-049 kuralı kaydın tarihini ne
 * yapar. Sunucudaki `tarihKurali` ile aynı hesap: güncel belgelerden (işaretlenen çıkar, geri alınan girer) türü BELGE_TARIH_TURU olanların
 * en yenisi; onun bitişi doluysa tarih o olur, değilse kayıttaki tarih kalır. Kaydın tarihi formdaki değerdir (elle yazılmış olabilir).
 */
export function yenisiGeldiTarihEtkisi(kutuk, kayitTarihi, guncel, belge, isaret) {
    const tur = BELGE_TARIH_TURU[kutuk];
    if (!tur)
        return null;
    const digerleri = guncel.filter((b) => b.id !== belge.id);
    const kume = isaret ? digerleri : [...digerleri, belge];
    const en = enYeniTarihBelgesi(kume.filter((b) => b.tur === tur));
    const gun = (v) => (v instanceof Date ? v.toISOString() : String(v)).slice(0, 10);
    const once = kayitTarihi ? gun(kayitTarihi) : null;
    return { alan: BELGE_TARIH_ALANI_ADI[kutuk], once, sonra: en?.gecerlilikSonu ? gun(en.gecerlilikSonu) : once };
}
/** D-074 Faz 2: işaretlenecek belgeden başka aynı türde güncel belge var mı (onay metninin uyarı cümlesi). */
export const ayniTurdeGuncelVarMi = (guncel, belge) => guncel.some((b) => b.id !== belge.id && b.tur === belge.tur);
/** ISO tarih ya da gün → "dd/mm/yyyy" (saat dilimine bakmadan; tarihler gün başı UTC yazılır). */
const gunMetni = (iso) => {
    const [y, a, g] = iso.slice(0, 10).split("-");
    return `${g}/${a}/${y}`;
};
/**
 * Tablo çipi: [kısa tür (po: "PO <poNo>" ya da "PO"), belgeNo ?? (sureklilik'te tarih), "p. a–b"].join(" · ").
 * Örn. "3.1 · p. 41–43", "3.1 · IC-2212146 · p. 1–2", "PO 2025-0417", "WPQR · WQ-2024-017 · p. 1", "Continuity · 15/09/2026 · p. 2",
 * "Calibration · SQM-26-0412 · p. 1–2". `s.cevir` yalnız kısa türe uygulanır.
 * D-064: yöntemi yazılı NDT sertifikasında kısa türün YERİNE yöntem (çevrilmez) ve, seviye doluysa, "Level <seviye>" (düz metin olarak
 * `s.cevir`'den geçer) yazılır: "MT · Level 2 · MT-L2-0418 · p. 1". Yöntemi boş (D-062 dönemi) sertifikada "NDT certificate · …" kalır.
 */
export function belgeCipi(b, s) {
    const cevir = (m) => (s?.cevir ? s.cevir(m) : m);
    const parcalar = [];
    if (b.tur === "ndt_sertifika" && b.yontem) {
        parcalar.push(b.yontem);
        if (b.seviye)
            parcalar.push(cevir(`Level ${b.seviye}`));
    }
    else {
        const kisa = cevir(BELGE_TURU_KISA[b.tur] ?? b.tur);
        parcalar.push(b.tur === "po" && b.poNo ? `${kisa} ${b.poNo}` : kisa);
    }
    const tarihMetni = s?.tarih ?? gunMetni;
    const ikinci = b.belgeNo || (b.tur === "sureklilik" && b.tarih ? tarihMetni(b.tarih) : null);
    if (ikinci)
        parcalar.push(ikinci);
    if (b.sayfaBas !== null && b.sayfaBas !== undefined) {
        parcalar.push(b.sayfaSon !== null && b.sayfaSon !== undefined && b.sayfaSon !== b.sayfaBas ? `p. ${b.sayfaBas}–${b.sayfaSon}` : `p. ${b.sayfaBas}`);
    }
    return parcalar.join(" · ");
}
/**
 * Güncel belgelere göre (kaldırılmamış, yerine yenisi gelmemiş; D-074 Faz 2) rozetler: stok_paket / sarf: 3.1, 3.2 ya da CoC yoksa "no certificate"; PO belgesi yoksa (stok_paket'te ek olarak
 * paketin PO no.'su da boşsa) "no PO"; kaynakci: wpq yoksa "no qualification document"; ndt_inspektor: ndt_sertifika yoksa
 * "no certificate"; alet: kalibrasyon yoksa "no calibration certificate"; wps: yok. Rozet engellemez (belge zorunlu değil).
 */
export function belgeRozetleri(kutuk, etkin, ek) {
    const var_ = (...turler) => etkin.some((b) => turler.includes(b.tur));
    const rozetler = [];
    if (kutuk === "stok_paket" || kutuk === "sarf") {
        if (!var_("en10204_31", "en10204_32", "coc"))
            rozetler.push("no certificate");
        if (!var_("po") && !(kutuk === "stok_paket" && ek?.poNo?.trim()))
            rozetler.push("no PO");
    }
    else if (kutuk === "kaynakci") {
        if (!var_("wpq"))
            rozetler.push("no qualification document");
    }
    else if (kutuk === "ndt_inspektor") {
        if (!var_("ndt_sertifika"))
            rozetler.push("no certificate");
    }
    else if (kutuk === "alet") {
        if (!var_("kalibrasyon"))
            rozetler.push("no calibration certificate");
    }
    return rozetler;
}
//# sourceMappingURL=kutuk-belge.js.map